import { Box, Stack, Text } from '@chakra-ui/core'
import { useFormikContext } from 'formik'
import React from 'react'

import {
  ConnectedFormGroup,
  ConnectedSelect,
  ConnectedDatePicker,
  ConnectedFileUploader,
  ConnectedNumberInput,
  ConnectedRadioGroup,
  ConnectedRating,
  ConnectedCheckboxGroup,
  ConnectedDatalist,
  ConnectedPhoneNumberInput,
  ConnectedMetricSpecific,
  ConnectedTextArea
} from '../../../../components/FormElements'
import { usePreviewerContext } from '../../../../context/PreviewerProvider'
import { EvidenceCustomDataFieldsDynamicZone, Maybe } from '../../../../generated/graphql'
import { parseDot } from '../../../../utils'
import { parseBound } from '../../utils'

type DataFieldsFieldsGeneratorProps = {
  fields: Maybe<EvidenceCustomDataFieldsDynamicZone>[]
  evidenceFields?: Maybe<EvidenceCustomDataFieldsDynamicZone>[]
}
const DataFieldsFieldsGenerator: React.FC<DataFieldsFieldsGeneratorProps> = ({
  fields,
  evidenceFields
}) => {
  const { dirty, values: formikValues } = useFormikContext()
  const values = (formikValues || {}) as any
  const { setFormIsDirty } = usePreviewerContext()

  React.useEffect(() => {
    setFormIsDirty?.(dirty)
  }, [dirty, setFormIsDirty])

  return (
    <Stack spacing={4} mb={4}>
      {fields?.map((field, index) => {
        if (!field) return null
        const name = `${field?.__typename}_${index}_${parseDot((field as any)?.label || '')}`

        switch (field.__typename) {
          case 'ComponentCustomDataFieldsText':
            return (
              <ConnectedTextArea
                key={index}
                label={labelBuilderWithRequiredStar(field.label || '', field.isRequired || false)}
                name={name}
              />
            )

          case 'ComponentCustomDataFieldsDataList':
            return (
              <ConnectedDatalist
                key={index}
                label={labelBuilderWithRequiredStar(field.label || '', field.isRequired || false)}
                name={name}
                listId={field.listId || ''}
              />
            )
          case 'ComponentCustomDataFieldsDateTimeField':
            return (
              <ConnectedDatePicker
                key={index}
                label={labelBuilderWithRequiredStar(field.label || '', field.isRequired || false)}
                name={name}
              />
            )
          case 'ComponentCustomDataFieldsEmailField':
            return (
              <ConnectedFormGroup
                key={index}
                label={labelBuilderWithRequiredStar(field.label || '', field.isRequired || false)}
                name={name}
                type="email"
              />
            )

          case 'ComponentCustomDataFieldsImageField':
            return (
              <ConnectedFileUploader
                key={index}
                label={labelBuilderWithRequiredStar(field.label || '', field.isRequired || false)}
                name={name}
                autoUpload
                allowedFileTypes={[
                  'image/*',
                  'application/pdf',
                  'application/msword',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                  'text/plain',
                  'text/tab-separated-values',
                  'text/csv'
                ]}
                placeholder="Upload  images"
                isMulti
                isEditing
              />
            )

          case 'ComponentCustomDataFieldsNumberField':
            return (
              <ConnectedNumberInput
                key={index}
                label={labelBuilderWithRequiredStar(field.label || '', field.isRequired || false)}
                name={name}
                min={parseBound(field.min)}
                max={parseBound(field.max)}
              />
            )
          case 'ComponentCustomDataFieldsMetricsSpecific':
            return (
              <ConnectedMetricSpecific
                key={index}
                metric={field.metric || ''}
                label={labelBuilderWithRequiredStar(field.label || '', field.isRequired || false)}
                name={name}
                type="text"
              />
            )
          case 'ComponentCustomDataFieldsPercentage':
            return (
              <ConnectedNumberInput
                key={index}
                label={labelBuilderWithRequiredStar(field.label || '', field.isRequired || false)}
                name={name}
                min={0}
                max={100}
                suffix="%"
              />
            )
          case 'ComponentCustomDataFieldsPhoneNumberField':
            const defaultValue =
              (
                evidenceFields?.find(
                  (e) =>
                    e?.__typename === 'ComponentCustomDataFieldsPhoneNumberField' &&
                    e.label === field.label
                ) as any
              )?.phoneNumber || ''

            return (
              <ConnectedPhoneNumberInput
                key={index}
                name={name}
                title={labelBuilderWithRequiredStar(field.label || '', field.isRequired || false)}
                defaultValue={defaultValue}
              />
            )
          case 'ComponentCustomDataFieldsRadio':
            return (
              <ConnectedRadioGroup
                key={index}
                label={labelBuilderWithRequiredStar(field.label || '', field.isRequired || false)}
                name={name}
                options={
                  field.option?.map((option) => ({
                    label: option?.option || '',
                    value: option?.option || ''
                  })) || []
                }
              />
            )
          case 'ComponentCustomDataFieldsRating':
            return (
              <ConnectedRating
                label={labelBuilderWithRequiredStar(field.label || '', field.isRequired || false)}
                name={name}
                value={values?.[name] || 0}
              />
            )
          case 'ComponentCustomDataFieldsSelect':
            return (
              <ConnectedSelect
                key={index}
                label={labelBuilderWithRequiredStar(field.label || '', field.isRequired || false)}
                name={name}
                options={
                  field.option?.map((option) => ({
                    label: option?.option || '',
                    value: option?.option || ''
                  })) || []
                }
              />
            )
          case 'ComponentCustomDataFieldsCheckbox':
            return (
              <ConnectedCheckboxGroup
                key={index}
                label={labelBuilderWithRequiredStar(field.label || '', field.isRequired || false)}
                name={name}
                options={
                  field.option?.map((option) => ({
                    label: option?.option || '',
                    value: option?.option || ''
                  })) || []
                }
              />
            )
          default:
            return (
              <Box key={index} mb={4}>
                <Text color="red.500" fontSize="sm">
                  Unsupported field type: {field.__typename}
                </Text>
              </Box>
            )
        }
      })}
    </Stack>
  )
}

const labelBuilderWithRequiredStar = (label: string, isRequired: boolean) => {
  return isRequired ? `${label} *` : label
}

export default DataFieldsFieldsGenerator
